import {DropDownList} from "@progress/kendo-react-dropdowns"
import {Field, FieldRenderProps} from "@progress/kendo-react-form"
import {Loader} from "@progress/kendo-react-indicators"
import {Input, InputChangeEvent} from "@progress/kendo-react-inputs"
import {Upload, UploadFileInfo, UploadOnAddEvent, UploadSelectMessageProps} from "@progress/kendo-react-upload"
import dayjs from "dayjs"
import React, {useRef, useState} from "react"
import {
    BookingDetail,
    deleteMotCertificate,
    downloadMotCertificate,
    fetchVehicleData,
    resetBookingVehicleSection,
    setBlobExists,
    uploadMotCertificate,
} from "../../../containers/MotBooking/bookingSlice"
import {RootState} from "../../../rootReducer"
import {CustomCalendar} from "../../../shared/CustomCalendar"
import {useAppDispatch, useAppSelector} from "../../../store"
import {selectTestCodeByVehicleType} from "../../../utils/common"
import {AnnualTest} from "../../../containers/Vehicles/vehicleSlice"

const VehicleSection = ({
    booking,
    updateBookingDetail,
    syncFormValues,
}: {
    booking: BookingDetail
    updateBookingDetail: (bookingDetail: BookingDetail) => void
    syncFormValues: (bookingDetail: BookingDetail) => BookingDetail
}) => {
    const {vehicleID, registration, dvsaFetchDate, newVehicle, annualTests} = booking

    const dispatch = useAppDispatch()

    const gettingVehicleData = useAppSelector((state: RootState) => state.containers.motBooking.gettingVehicleData)
    const loadingMakes = useAppSelector((state: RootState) => state.containers.motBooking.loadingMakes)
    const loadingModels = useAppSelector((state: RootState) => state.containers.motBooking.loadingModels)

    const loadingVehicleClass = useAppSelector((state: RootState) => state.containers.motBooking.loadingVehicleClass)
    const loadingVehicleType = useAppSelector((state: RootState) => state.containers.motBooking.loadingVehicleType)

    const makesList = useAppSelector((state: RootState) => state.containers.motBooking.makeList)
    const modelsList = useAppSelector((state: RootState) => state.containers.motBooking.modelsList)
    const vehicleClasses = useAppSelector((state: RootState) => state.containers.motBooking.vehicleClasses)
    const vehicleTypes = useAppSelector((state: RootState) => state.containers.motBooking.vehicleTypes)
    const doesBlobExists = useAppSelector((state: RootState) => state.containers.motBooking.blobExists)
    const downloadCertificateLoader = useAppSelector((state: RootState) => state.containers.motBooking.downloadloader)
    const deleteCertificateLoader = useAppSelector((state: RootState) => state.containers.motBooking.deleteloader)

    const existsloader = useAppSelector((state: RootState) => state.containers.motBooking.existsloader)
    const getToken = useAppSelector((state: RootState) => state.application.accessToken)
    const selectedRegionId = useAppSelector((state: RootState) => state.application.selectedRegion.regionId)
    const selectedBookingId = useAppSelector((state: RootState) => state.containers.motBooking.selectedBookingId)
    const regNumberField = useRef<any>()
    
    const [files, setFiles] = React.useState<Array<UploadFileInfo>>([]);

    const disableVehicleInput = dvsaFetchDate != null || ((vehicleID === null || vehicleID === 0) && !newVehicle)
    const disableVehicleTypeInput = (vehicleID === null || vehicleID === 0) && !newVehicle
    const passedAnnualTest = annualTests?.find(annualTest => annualTest.testResult === "PASSED")

    const VehRegInput = (fieldRenderProps: FieldRenderProps) => {
        const {name, label, disabled} = fieldRenderProps
        const [registrationNumber, setRegistrationNumber] = useState<string>(registration)

        const onValueChange = (event: InputChangeEvent) => {
            setRegistrationNumber(event.value.replace(/\s/g, "").toUpperCase())
        }

        return (
            <>
                <Input
                    id={`bkveh_input_${name}`}
                    ref={regNumberField}
                    name={name}
                    label={label}
                    value={registrationNumber}
                    onChange={onValueChange}
                    disabled={disabled}
                />
            </>
        )
    }

    const VehicleDropdownField = (fieldRenderProps: FieldRenderProps) => {
        const {name, label, required, value, data, onChange, disabled, loading} = fieldRenderProps
        const onValueChange = React.useCallback(
            event => {
                onChange(event.target.value)
                let updateBooking = booking
                if (name === "make") {
                    updateBooking = {...booking, [name]: event.target.value, model: ""}
                } else if (name === "vehicleType") {
                    updateBooking = {
                        ...booking,
                        [name]: event.target.value,
                        testCode: selectTestCodeByVehicleType(event.target.value ?? ""),
                        axleCode: null,
                        testPackageID: null,
                    }
                } else {
                    updateBooking = {...booking, [name]: event.target.value}
                }
                updateBookingDetail(updateBooking)
            },
            [name, onChange],
        )

        return (
            <>
                <DropDownList
                    required={required}
                    value={value}
                    id={`bkveh_dpdw_${name}`}
                    onChange={onValueChange}
                    data={data}
                    label={label}
                    disabled={gettingVehicleData || disabled}
                    loading={gettingVehicleData || loading}
                />
            </>
        )
    }

    const updateManufactureDate = (date: any) => {
        updateBookingDetail({...booking, manufactureDate: date})
    }
    const updateRegistrationDate = (date: any) => {
        updateBookingDetail({...booking, registrationDate: date})
    }
    const updateAnnualTestExpiryDate = (date: any) => {
        updateBookingDetail({...booking, annualTestExpiryDate: date})
    }

    const resetVehicleData = () => {
        const data = syncFormValues(booking)
        dispatch(resetBookingVehicleSection(data))
        dispatch(setBlobExists(false))
    }

    const onVehicleSearch = () => {
        const registration = regNumberField.current.value
        const data = syncFormValues(booking)
        dispatch(fetchVehicleData(registration, data))
    }

    const customMessage = (props: UploadSelectMessageProps) => {
        let {message} = props
        message = (
            <>
                <span className="k-icon k-i-upload me-2"></span>
                <span>Certificate</span>
            </>
        )
        return message
    }

    const LoadingMOTAnnualtestPanel = () => (
        <div className="position-relative">
            <div className="d-flex justify-content-center">
                <div className="k-loading-mask" style={{fontSize: "12px"}}>
                    <span className="k-loading-text">Loading</span>
                    <div className="k-loading-image"></div>
                    <div className="k-loading-color"></div>
                </div>
            </div>
        </div>
    )

    function onSaveRequest(
        files: UploadFileInfo[],
        options: { formData: FormData; requestOptions: any },
        onProgress: (uid: string, event: ProgressEvent<EventTarget>) => void
      ): Promise<{ uid: string }> {
        const currentFile = files[0] as UploadFileInfo;
        const uid = currentFile.uid;
        
        return new Promise<{ uid: string }>((resolve, reject) => {
          if (
            currentFile.validationErrors &&
            currentFile.validationErrors.length > 0
          ) {
            reject({ uid: uid });
          } else {
            const reader = new FileReader();
            reader.onload = () => {
              if (reader.result && typeof reader.result === "string") {
                const base64Result = reader.result.split(",")[1];
                dispatch(uploadMotCertificate(base64Result, currentFile.name ,selectedRegionId ,selectedBookingId));
                resolve({ uid: uid });
              } else {
                reject({ uid: uid });
              }
            };
            reader.readAsDataURL(currentFile.getRawFile!());
          }
        });
      }
  
    return (
        <>
            <div className="text-muted font-weight-bold">Vehicle</div>
            <hr className="title-hr mt-0" />
            <div style={{display: "flex", alignItems: "flex-end"}}>
                <Field
                    tabIndex={1}
                    name="registration"
                    component={VehRegInput}
                    label={"Registration / C-Number"}
                    disabled={(booking.vehicleID != null && booking.vehicleID > 0) || booking.newVehicle}
                />
                <button
                    tabIndex={2}
                    type="button"
                    className="k-button k-button-md k-button-rectangle k-button-flat k-button-flat-primary"
                    disabled={gettingVehicleData}
                    onClick={() => {
                        ;(booking.vehicleID != null && booking.vehicleID > 0) || booking.newVehicle
                            ? resetVehicleData()
                            : onVehicleSearch()
                    }}
                >
                    {(booking.vehicleID != null && booking.vehicleID > 0) || booking.newVehicle ? (
                        <span className="k-icon k-i-close"></span>
                    ) : (
                        <span className="k-icon k-i-search"></span>
                    )}
                </button>
            </div>
            <Field
                tabIndex={3}
                name="make"
                component={VehicleDropdownField}
                label="Make"
                data={makesList}
                disabled={disableVehicleInput}
                loading={loadingMakes}
            />
            <Field
                tabIndex={3}
                name="model"
                component={VehicleDropdownField}
                label="Model"
                data={modelsList}
                disabled={disableVehicleInput}
                loading={loadingModels}
            />
            <Field
                tabIndex={4}
                name="vehicleType"
                component={VehicleDropdownField}
                label="Vehicle Type"
                data={vehicleTypes}
                disabled={disableVehicleTypeInput}
                loading={loadingVehicleType}
                required={registration != null && registration.trim() !== ""}
            />
            <Field
                tabIndex={5}
                name="vehicleClass"
                component={VehicleDropdownField}
                label="Class"
                data={vehicleClasses}
                disabled={disableVehicleInput}
                loading={loadingVehicleClass}
            />
            <Field
                name="manufactureDate"
                component={CustomCalendar}
                updateDate={updateManufactureDate}
                label="Manufactured"
                // value={booking.manufactureDate}
                disabled={disableVehicleInput}
            />
            <Field
                name="registrationDate"
                component={CustomCalendar}
                label="Registration"
                updateDate={updateRegistrationDate}
                // value={bookingDetail.registrationDate}
                disabled={disableVehicleInput}
            />
            <Field
                name="annualTestExpiryDate"
                component={CustomCalendar}
                updateDate={updateAnnualTestExpiryDate}
                label="Annual Test Exp."
                // value={bookingDetail.annualTestExpiryDate}
                disabled={disableVehicleInput}
            />
            {annualTests && annualTests.length > 0 && (
                <div className="pt-1 motupload relative" data-testid="annualTestList">
                    <div className="row border align-middle pb-2 pt-2" style={{margin: "0 0 0.7rem 0"}}>
                        {annualTests.map((annualTest: AnnualTest) => (
                            <>
                                <div
                                    className={`d-flex justify-content-between ${
                                        annualTest.testResult === "FAILED" ? "mb-2" : ""
                                    }`}
                                >
                                    <span className="text-muted" title="Certificate number">
                                        {annualTest.testCertificateNumber != null
                                            ? annualTest.testCertificateNumber
                                            : "N/A"}
                                    </span>
                                    <span
                                        className={`font-weight-bold text-uppercase text-${
                                            annualTest.testResult === "PASSED" ? "success" : "danger"
                                        } m-0`}
                                        title="Test result"
                                    >
                                        {annualTest.testResult}
                                    </span>
                                </div>
                                {annualTest.expiryDate !== null && annualTest.testResult !== "FAILED" && (
                                    <div className="d-flex justify-content-between ">
                                        <span className="text-muted" title="Expiry date">
                                            {dayjs(annualTest.expiryDate).format("DD/MM/YYYY")}
                                        </span>
                                        <span>
                                            {doesBlobExists && (
                                                <>
                                                    {downloadCertificateLoader ? (
                                                        <Loader className="ms-2" size="small" type="pulsing" />
                                                    ) : (
                                                        <span
                                                            className=" fa fa-cloud-download-alt c-pointer"
                                                            onClick={(e: any) =>
                                                                dispatch(
                                                                    downloadMotCertificate(
                                                                        `${regNumberField.current.value}-${
                                                                            passedAnnualTest !== undefined
                                                                                ? passedAnnualTest.testCertificateNumber
                                                                                : ""
                                                                        }`,
                                                                    ),
                                                                )
                                                            }
                                                        ></span>
                                                    )}
                                                    {deleteCertificateLoader ? (
                                                        <Loader className="ms-2" size="small" type="pulsing" />
                                                    ) : (
                                                        <span
                                                            className="fa fa-trash ms-2 me-1 c-pointer"
                                                            onClick={(e: any) =>
                                                                dispatch(
                                                                    deleteMotCertificate(
                                                                        `${regNumberField.current.value}-${
                                                                            passedAnnualTest !== undefined
                                                                                ? passedAnnualTest.testCertificateNumber
                                                                                : ""
                                                                        }`,
                                                                    ),
                                                                )
                                                            }
                                                        ></span>
                                                    )}
                                                </>
                                            )}
                                        </span>
                                    </div>
                                )}
                            </>
                        ))}
                    </div>
                    {passedAnnualTest !== undefined && passedAnnualTest.testResult !== "FAILED" && (
                        <div className="col-12"  data-testid="passedAnnualTestList">
                            {existsloader && <LoadingMOTAnnualtestPanel />}
                            {!doesBlobExists && (
                                <Upload
                                    selectMessageUI={customMessage}
                                    files={files}
                                    autoUpload={true}
                                    batch={false}
                                    multiple={false}
                                    restrictions={{
                                        allowedExtensions: [".pdf"],
                                    }}
                                    withCredentials={false}
                                    onAdd={(event: UploadOnAddEvent) => {
                                        event.affectedFiles[0].name = `${regNumberField.current.value}-${passedAnnualTest !== undefined ? passedAnnualTest.testCertificateNumber : ""
                                            }`
                                        setFiles(event.newState);
                                    }}
                                    onStatusChange={(event: any) => {
                                        if (event.affectedFiles[0].status === 4) {
                                            dispatch(setBlobExists(true))
                                        }
                                    }}
                                    saveUrl={onSaveRequest}
                                    saveHeaders={{
                                        Authorization: `Bearer ${getToken}`,
                                    }}
                                />
                            )}
                        </div>
                    )}
                </div>
            )}
        </>
    )
}

export default React.memo(VehicleSection)
